import { orderBy, partition } from 'lodash-es';
import { marked } from 'marked';
import sanitize from 'sanitize-html';
import type { Schema } from './json-ld';

export enum ImageSize {
	THUMBNAIL = 'thumbnail',
	SMALL = 'small',
	MEDIUM = 'medium',
	LARGE = 'large',
	NONE = ''
}

export const pathMatch = (path: string, url: string, exact = false) => {
	if (url === '/') return path === url;
	// Remove trailing slash
	const urlWithoutTrailingSlash = url.endsWith('/') ? url.slice(0, -1) : url;
	return exact ? path === urlWithoutTrailingSlash : path.startsWith(urlWithoutTrailingSlash);
};

export const getImageUrl = (image: BaseUploadFile, size: ImageSize = ImageSize.NONE) => {
	if (!image) return '';
	let imgUrl = image.url;
	if (size !== ImageSize.NONE) {
		imgUrl = image.formats?.[size]?.url || image.url;
	}
	const url = new URL(imgUrl);
	if (!url.hostname.startsWith('cdn')) {
		imgUrl = imgUrl.replace(url.hostname, `cdn.${url.hostname}`);
	}
	return imgUrl;
};

export const calculateRgba: (color: string, opacity: number) => string = (color, opacity) => {
	if (color.startsWith('#')) {
		color = color.slice(1);
	}
	if (color.length === 3) {
		let res = '';
		color.split('').forEach((c: string) => {
			res += c;
			res += c;
		});
		color = res;
	}
	const rgbValues = (color.match(/.{2}/g) ?? []).map((hex: string) => parseInt(hex, 16)).join(', ');
	return `rgba(${rgbValues}, ${opacity})`;
};
export const range: (size: number, startAt?: number) => number[] = (size, startAt = 0) =>
	[...Array(size).keys()].map((i) => i + startAt);

export const getYoutubeId: (url: string) => string = (url) => {
	return new URL(url).pathname.split('/').pop() ?? '';
};

export function splitSentence(sentence: string, splitLength: number, splitCount: number): string[] {
	// Split the sentence into an array of words
	const words = sentence.split(' ');

	// Initialize an empty array to store the lines
	const lines: string[] = [];

	// Initialize a string to store the current line
	let line = '';

	// Initialize a counter to keep track of the number of lines
	let lineCount = 0;

	// Iterate over the words in the sentence
	while (words.length > 0 && lineCount < splitCount) {
		// Pop the first word off the front of the array
		const word = words[0];

		// If the current line + the current word is longer than the split length, or if the line count is equal to the split count, push the current line to the lines array and reset the current line
		if (line.length + word.length > splitLength && line !== '') {
			lines.push(line);
			line = '';
			lineCount++;
		} else {
			// Add the current word to the current line
			line += `${words.shift()} `;
		}
	}

	// Push the remaining line to the lines array
	if (line.length > 0) lines.push(line);

	// If there are words remaining after the sentence is split, add ellipsis to the last line
	if (words.length > 0) {
		const lastLine = lines[lines.length - 1];
		lines[lines.length - 1] =
			lastLine.length >= splitLength
				? `${lastLine.slice(0, splitLength - 3)}...`
				: `${lastLine}...`;
	}

	// Return the lines array
	return lines;
}

export async function getUserCountryCode(): Promise<string> {
	return fetch('https://ipwho.is')
		.then((res) => res.json() as unknown as { country_code: string })
		.then((res) => res.country_code);
}

export function valueComparisonAdjective(value: number, comparisonValue: number): string {
	if (value > comparisonValue) return 'higher than';
	if (value < comparisonValue) return 'lower than';
	return 'inline with';
}

export function filterOutliers(someArray: number[]): number[] {
	const iqr = getIqr(someArray);

	return someArray.filter((x) => x >= iqr.lowerQuartile && x <= iqr.upperQuartile);
}

export function getIqr(someArray: number[]) {
	const values = someArray.slice().sort((a, b) => a - b);
	if (values.length < 4) {
		return {
			upperQuartile: values[values.length - 1],
			lowerQuartile: values[0]
		};
	}

	let q1: number;
	let q3: number;
	if ((values.length / 4) % 1 === 0) {
		q1 = (1 / 2) * (values[values.length / 4] + values[values.length / 4 + 1]);
		q3 = (1 / 2) * (values[values.length * (3 / 4)] + values[values.length * (3 / 4) + 1]);
	} else {
		q1 = values[Math.floor(values.length / 4 + 1)];
		q3 = values[Math.ceil(values.length * (3 / 4) + 1)];
	}

	const iqr = q3 - q1;
	const upperQuartile = q3 + iqr * 1.5;
	const lowerQuartile = q1 - iqr * 1.5;

	return {
		upperQuartile,
		lowerQuartile
	};
}

export function getDeviceCategory(): string {
	const width = window.innerWidth;
	let screenType: string;
	if (width <= 520) {
		screenType = 'mobile';
	} else if (width <= 820) {
		screenType = 'tablet';
	} else {
		screenType = 'desktop';
	}
	return screenType;
}

export const format = (node: HTMLInputElement, formatFunction: (value: string) => string) => {
	function updateValue() {
		node.value = formatFunction(node.value);
	}

	node.addEventListener('input', updateValue);
	node.addEventListener('paste', updateValue);

	// Format on intial hydration
	node.value = formatFunction(node.value);

	return {
		destroy() {
			node.removeEventListener('input', updateValue);
			node.removeEventListener('paste', updateValue);
		}
	};
};

export const stripHtml = (html: string) => {
	return sanitize(html, { allowedTags: [], allowedAttributes: {} });
};

export function getBreadcrumbSchema(
	breadcrumbs: NonNullable<App.PageData['breadcrumbs']>,
	url: URL
): Schema {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [{ path: '/', title: 'Home' }, ...breadcrumbs].map((breadcrumb, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: breadcrumb.title,
			...(index < breadcrumbs.length
				? {
						item: `${url.origin}${breadcrumb.path}`
					}
				: {})
		}))
	};
}

export function markedParse(markdown: string): string | Promise<string> {
	const renderer = new marked.Renderer();
	const linkRenderer = renderer.link;
	renderer.link = (link) => {
		const localLink = link.href.startsWith(`${location.protocol}//${location.hostname}`);
		const html = linkRenderer.call(renderer, link);
		return localLink
			? html
			: html.replace(/^<a /, `<a target="_blank" rel="noreferrer noopener nofollow" `);
	};
	return marked(markdown, {
		renderer
	});
}

export function getSignedColor(x: number): string {
	if (x > 0) return 'green-ticker';
	else if (x < 0) return 'red-ticker';
	else return 'gray-dark';
}

export function wordsFromUid(str: string) {
	return str.replaceAll('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export function uidFromWords(str: string) {
	return str.toLowerCase().replaceAll(' ', '-');
}

export function sortBy<T>(rows: T[], key: keyof T, direction: 'asc' | 'desc'): T[] {
	const [hasValue, noValue] = partition(rows, (row) => row[key]);
	return orderBy(hasValue, key, direction).concat(noValue);
}

export function preprocessContent(content: string, variables?: Record<string, string>) {
	if (!variables) return content;
	return content.replace(/\{\{([^}]+)\}\}/g, (match, p1) => variables[p1] || match);
}
